import React from 'react';
import { useCookies } from 'react-cookie';
import { CssReset, palette } from '@solvhealth/jigsaw-v1';
import Hidden from '@material-ui/core/Hidden';
import { colors } from '~/constants/colors';
import { campaignNames } from '~/core/analytics/events';
import history from '../../core/history';
import useSetNativeSafeAreaColor from '../../hooks/useSetNativeSafeAreaColor';
import DownloadDriver from '../DownloadDriver';
import SuccessDialog from '../Membership/Signup/SuccessDialog';
import AccountNav from '../SolvPatternLibrary/AccountNav';
import useEffectSetActiveAccountProfile from './components/GetCare/hooks/useEffectSetActiveAccountProfile';
import Header from './components/Header';
import useAccountView from './hooks/useAccountView';
import useProfileId from './hooks/useProfileId';
import useSetOrigin from './hooks/useSetOrigin';
import { useShowFullScreenPrompt } from './hooks/useShowFullScreenPrompt';
import { useShowMissingNotificationPrompt } from './hooks/useShowMissingNotificationsPrompt';
import {
  FLU_SHOT_DRIVER_DISMISSED_COOKIE,
  FLU_SHOT_MODAL_FEATURE_FLAG,
  FLU_SHOT_MODAL_ID,
  FluShotModal,
} from '~/components/FluShotModal';
import { useSolvSelector } from '~/reducers';
import BorderBoxReset from '../util/BorderBoxReset';
import { isActiveMembership } from '~/core/util/account';
import { useBottomNavHeight } from '~/components/SolvPatternLibrary/AccountNav/hooks/useBottomNavHeight';
import useInitialWidth from '~/hooks/useInitialWidth';
import AccountViews from '~/components/AccountGetsMarried/constants/AccountViews';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { RemixRoutes } from '~/core/remix';
import { analyticsTrackEvent } from '~/core/analytics';

interface Props {
  context?: {
    route: {
      view: string;
    };
  };
  params?: any;
}

export const getUtmQueryString = () => {
  // Build search params object from current URL
  const searchParams = new URLSearchParams(window.location.search);

  // Filter to only utm params
  const utmParams: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      utmParams[key] = value;
    }
  });

  // Build new query string
  const newQueryString = new URLSearchParams(utmParams).toString();

  return newQueryString ? `?${newQueryString}` : ''; // I'm using utm source as a proxy for whether there are utm params in the query string, not 100% sure that's a good idea
};

const useRedirectToTurbo = (tab: AccountViews) => {
  const {
    merry486TurboAppHome,
    merry486TurboVisitsTab,
    merry486TurboInsuranceTab,
    merry486TurboProfileTab,
  } = useLDFlags();

  switch (tab) {
    case AccountViews.Manage:
      if (merry486TurboVisitsTab) {
        window.location.href = RemixRoutes.appTabs.visits + getUtmQueryString();
        return true;
      }
      break;

    case AccountViews.Insurance:
      if (merry486TurboInsuranceTab) {
        window.location.href = RemixRoutes.appTabs.insurance + getUtmQueryString();
        return true;
      }
      break;

    case AccountViews.Profile:
      if (merry486TurboProfileTab) {
        window.location.href = RemixRoutes.appTabs.profile + getUtmQueryString();
        return true;
      }
      break;

    case AccountViews.GetCare:
    default:
      analyticsTrackEvent('Get care tab redirect', {
        redirect: merry486TurboAppHome,
      });
      if (merry486TurboAppHome) {
        window.location.href = RemixRoutes.appTabs.home + getUtmQueryString();
        return true;
      }
  }

  return false;
};

const AccountGetsMarried = (props: Props) => {
  const { params, context } = props;
  useSetOrigin();
  const View = useAccountView(context?.route.view);
  const userProfileId = useProfileId(params);
  useEffectSetActiveAccountProfile(userProfileId);
  useSetNativeSafeAreaColor(
    isActiveMembership(useSolvSelector((state) => state.account.summary))
      ? palette.teal.xlight
      : colors.marriedToPurple
  );
  useShowFullScreenPrompt();
  useShowMissingNotificationPrompt();
  const [cookies] = useCookies();
  const bottomNavHeight = useBottomNavHeight();
  const initialWidth = useInitialWidth();

  const isFluShotModalFeatureOn = FLU_SHOT_MODAL_FEATURE_FLAG;
  const hasUserDismissedFluShotDriver = cookies[FLU_SHOT_DRIVER_DISMISSED_COOKIE];
  const shouldSuppressDownloadDriver = useSolvSelector(
    (state) =>
      !!(
        state.runtime.modalsShownInSession && state.runtime.modalsShownInSession[FLU_SHOT_MODAL_ID]
      )
  );
  const shouldShowFluShotDriver = isFluShotModalFeatureOn && !hasUserDismissedFluShotDriver;

  if (!params.userProfileId && context?.route.view !== 'settings') {
    history.replace(`/account/home/${userProfileId}`);
    return null;
  }

  return (
    <BorderBoxReset style={{ height: '100%', position: 'relative' }}>
      {shouldShowFluShotDriver && <FluShotModal userProfileId={userProfileId} />}
      {!shouldSuppressDownloadDriver && (
        <DownloadDriver campaignName={campaignNames.ACCOUNT_BOTTOM_CAROUSEL} isCarousel />
      )}
      <AccountNav viewName={context?.route.view} />
      <Header userProfileId={userProfileId} viewName={context?.route.view} />
      <View params={params} />
      <SuccessDialog />
      <CssReset />
      <Hidden initialWidth={initialWidth} mdUp>
        <div style={{ height: bottomNavHeight }} />
      </Hidden>
    </BorderBoxReset>
  );
};

const AccountGetsMarriedRedirectWrapper = (props: Props) => {
  const isRedirecting = useRedirectToTurbo(props.context?.route.view as unknown as AccountViews);

  if (isRedirecting) {
    return null;
  }

  return <AccountGetsMarried {...props} />;
};

export default AccountGetsMarriedRedirectWrapper;
